/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable quotes */
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./style.scss";
import {
  Row,
  Col,
  Button,
  message,
  Card,
  Space,
  Typography,
  Tag,
  Upload,
  Divider,
  Form,
  Input,
  Select,
  Modal,
  Spin,
} from "antd";
import API from "../../../api";
import urls from "../../../api/urls";
import {
  UploadOutlined,
  ScanOutlined,
  FileImageOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
// import { PDFDocument } from "pdf-lib"; // TODO: Install pdf-lib: npm install pdf-lib

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// PDF processing utility
const splitPDFPages = async (file, drsNumber) => {
  try {
    // Import pdf-lib dynamically
    const { PDFDocument } = await import("pdf-lib");
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pageCount = pdfDoc.getPageCount();

    const pages = [];

    for (let i = 0; i < pageCount; i++) {
      const newPdf = await PDFDocument.create();
      const [page] = await newPdf.copyPages(pdfDoc, [i]);
      newPdf.addPage(page);

      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      pages.push({
        pageNumber: i + 1,
        blob,
        url,
        originalFileName: `${drsNumber}-${Date.now()}.pdf`,
      });
    }

    message.success(`PDF split into ${pageCount} pages successfully`);

    return { pages, totalPages: pageCount };
  } catch (error) {
    message.error(
      "Failed to process PDF file. Please ensure it's a valid PDF."
    );
    throw new Error("Failed to process PDF file");
  }
};

// Calculate which page a shipment belongs to based on scannedIndex
const getShipmentPage = (scannedIndex) => {
  return Math.floor(scannedIndex / 12) + 1;
};

// Group shipments by page
const groupShipmentsByPage = (shipments) => {
  const pages = {};

  shipments.forEach((shipment) => {
    const page = getShipmentPage(shipment.scannedIndex || 0);
    if (!pages[page]) {
      pages[page] = [];
    }
    pages[page].push(shipment);
  });

  return pages;
};

const CpDrsDeliveryUpdate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Get data from navigation state
  const {
    selectedShipments = [],
    drsNumber = "",
    sewaSetuDrsNo = "",
    status = "delivered",
    remarks = "",
    receiverName = "",
    receiverPhone = "",
    daId = "",
  } = location.state || {};

  // State management
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [shipmentPages, setShipmentPages] = useState({});
  const [pageFiles, setPageFiles] = useState({}); // {pageNumber: {file: File, url: string}}
  const [pageS3Urls, setPageS3Urls] = useState({}); // {pageNumber: s3Url}
  const [uploadingPod, setUploadingPod] = useState({}); // {pageNumber: boolean}
  const [pdfProcessing, setPdfProcessing] = useState(false);
  const [geolocation, setGeolocation] = useState(null);

  // Form validation rules
  const validationRules = {
    receiverName: [
      { required: true, message: "Please enter receiver name" },
      { min: 2, message: "Name must be at least 2 characters" },
    ],
    receiverMobile: [
      { required: true, message: "Please enter receiver mobile number" },
      {
        pattern: /^[0-9]{10}$/,
        message: "Please enter a valid 10-digit mobile number",
      },
    ],
    reasonCode: [{ required: true, message: "Please select a reason" }],
    remarks: [
      {
        min: 5,
        message: "Remarks must be at least 5 characters when provided",
      },
    ],
  };

  // Fetch grouped orders from API
  const fetchGroupedOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.Api(`${urls.DRS_GROUPED_ORDERS}?drs=${drsNumber}`).get();
      
      if (response.status && response.response?.data?.data?.shipment_groups) {
        const groups = response.response.data.data.shipment_groups;
        
        // Transform API response to shipmentPages format
        const pages = {};
        groups.forEach((group) => {
          // Include all pages, even if they have empty AWB arrays
          pages[group.page_number] = (group.awb_numbers || []).map((awb) => ({
            cAWB_No: awb,
            // Add other properties that might be needed for display
          }));
        });
        
        setShipmentPages(pages);
      }
    } catch (error) {
      message.error(`Failed to fetch shipment groups: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [drsNumber]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeolocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Error getting location:", error);
          // Set default coordinates if location access is denied
          setGeolocation({
            latitude: 0,
            longitude: 0,
          });
        }
      );
    } else {
      setGeolocation({
        latitude: 0,
        longitude: 0,
      });
    }
  }, []);

  // Initialize component
  useEffect(() => {
    if (!drsNumber) {
      message.error(
        "Invalid access. DRS number is required."
      );
      navigate(-1);
      return;
    }

    // Fetch grouped orders from API
    fetchGroupedOrders();

    // Pre-fill form with values from modal if available
    if (receiverName || receiverPhone || remarks) {
      form.setFieldsValue({
        receiverName: receiverName,
        receiverMobile: receiverPhone,
        remarks: remarks,
      });
    }

    // Get current location
    getCurrentLocation();
  }, [
    drsNumber,
    navigate,
    getCurrentLocation,
    receiverName,
    receiverPhone,
    remarks,
    form,
    fetchGroupedOrders,
  ]);

  // Handle PDF upload and processing
  const handlePDFUpload = async (file) => {
    try {
      setPdfProcessing(true);

      // Split PDF into individual pages
      const { pages, totalPages } = await splitPDFPages(file, drsNumber);

      // Map pages to shipment pages
      const newPageFiles = {};
      pages.forEach((page, index) => {
        const pageNumber = index + 1;
        if (shipmentPages[pageNumber]) {
          newPageFiles[pageNumber] = {
            file: new File([page.blob], page.originalFileName, {
              type: "application/pdf",
            }),
            url: page.url,
            type: "pdf",
          };
        }
      });

      setPageFiles((prev) => ({ ...prev, ...newPageFiles }));
      message.success(
        `PDF processed successfully. ${
          Object.keys(newPageFiles).length
        } pages assigned to shipments.`
      );
    } catch (error) {
      message.error(error.message || "Failed to process PDF file");
    } finally {
      setPdfProcessing(false);
    }
  };

  // Handle individual page file upload
  const handlePageFileUpload = async (pageNumber, file) => {
    try {
      setUploadingPod((prev) => ({ ...prev, [pageNumber]: true }));
      
      // Upload to S3 first
      const s3Url = await uploadFileToServer(file, `${drsNumber}-${Date.now()}`);
      
      // Store both local preview and S3 URL
      const url = URL.createObjectURL(file);
      setPageFiles((prev) => ({
        ...prev,
        [pageNumber]: {
          file,
          url,
          type: file.type.includes("pdf") ? "pdf" : "image",
        },
      }));
      
      setPageS3Urls((prev) => ({
        ...prev,
        [pageNumber]: s3Url,
      }));
      
      message.success(`POD uploaded for page ${pageNumber}`);
    } catch (error) {
      message.error(`Failed to upload POD for page ${pageNumber}: ${error.message}`);
    } finally {
      setUploadingPod((prev) => ({ ...prev, [pageNumber]: false }));
    }
  };

  // Remove file from page
  const removePageFile = (pageNumber) => {
    setPageFiles((prev) => {
      const updated = { ...prev };
      if (updated[pageNumber]?.url) {
        URL.revokeObjectURL(updated[pageNumber].url);
      }
      delete updated[pageNumber];
      return updated;
    });
    
    setPageS3Urls((prev) => {
      const updated = { ...prev };
      delete updated[pageNumber];
      return updated;
    });
    
    message.success(`File removed from page ${pageNumber}`);
  };

  // Upload file to server and get URL
  const uploadFileToServer = async (file, shipmentNumber) => {
    const formData = new FormData();

    const fileName = `${shipmentNumber}.${file.name
      .split(".")
      .pop()
      .toLowerCase()}`;

    // Append file with proper structure
    formData.append("files", file, fileName);
    formData.append("path", "/drs_pod");
    formData.append("fileType", file.type);
    formData.append("filename", fileName);

    const response = await API.Api(urls.DRS_UPLOAD_POD).post(formData);

    if (response.status && response.response?.data?.data?.[0]?.url) {
      return response.response.data.data[0].url;
    } else {
      throw new Error("Failed to upload file");
    }
  };

  // Prepare shipment groups for POD upload API payload
  const prepareShipmentGroups = () => {
    const shipmentGroups = [];

    for (const [pageNumber, shipments] of Object.entries(shipmentPages)) {
      const awbNumbers = shipments
        .map((s) => s.cAWB_No)
        .filter((awb) => awb)
        .map((awb) => String(awb));
      
      const group = { awb_numbers: awbNumbers };

      // Add POD URL if available for this page
      if (pageS3Urls[pageNumber]) {
        group.pod_urls = [pageS3Urls[pageNumber]]; // Array format as required by API
      }

      shipmentGroups.push(group);
    }

    return shipmentGroups;
  };

  // Handle POD upload submission
  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      // Validate required POD files for all pages
      const pagesWithoutFiles = Object.keys(shipmentPages).filter(
        (page) => !pageS3Urls[page]
      );
      if (pagesWithoutFiles.length > 0) {
        // message.error(
        //   `Please upload POD files for pages: ${pagesWithoutFiles.join(", ")}`
        // );
        message.error(
          `POD must be uploaded for all pages.`
        );
        return;
      }

      // Prepare shipment groups with S3 URLs
      const shipmentGroups = prepareShipmentGroups();

      // Prepare API payload for updatePOD API (POD upload only)
      const payload = {
        da_id: daId,
        drs_number: drsNumber,
        source: "NBA_WEB",
        event_timestamp: new Date().toISOString(),
        shipment_groups: shipmentGroups,
      };

      // Make API call to updatePOD endpoint
      const response = await API.Api(urls.DRS_UPDATE_POD_V2).postIdHeader(
        payload
      );

      if (
        response.status &&
        (response.response?.status === 200 || response.response?.status === 201)
      ) {
        message.success("POD Uploaded Successfully!");

        // Navigate back to DRS details page
        navigate(-1);
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to Upload POD"
        );
      }
    } catch (error) {
      message.error(error.message || "Failed to Upload POD");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Render shipment page card
  const renderShipmentPage = (pageNumber, shipments) => {
    const pageFile = pageFiles[pageNumber];
    const hasFile = !!pageFile;
    const hasS3Url = !!pageS3Urls[pageNumber];

    return (
      <Card
        key={pageNumber}
        className={`shipment-page-card ${hasS3Url ? 'has-file' : ''} pod-upload-mode`}
        title={
          <div className="page-header">
            <div className="page-info">
              <div className="page-number">
                <Text strong className="page-text">Page {pageNumber}</Text>
                {hasS3Url && (
                  <CheckCircleOutlined className="file-check-icon" />
                )}
              </div>
              <Tag color={hasS3Url ? "success" : "blue"} className="shipment-count">
                {shipments.length} Shipment{shipments.length !== 1 ? 's' : ''}
              </Tag>
            </div>
          </div>
        }
        extra={null}
        bordered={false}
      >
        {/* Shipments list */}
        <div className="shipments-list">
          <div className="shipments-header">
            <Text type="secondary" className="shipments-label">
              Document Numbers:
            </Text>
          </div>
          <div className="shipments-grid">
            {shipments.map((shipment, index) => {
              const awbNumber = shipment?.cAWB_No || `Shipment ${index + 1}`;

              return (
                <div key={index} className="shipment-chip-wrapper">
                  <Tag
                    className={`shipment-chip ${shipment.shippingType === "REVERSE" ? "rto-shipment" : ""}`}
                    color={
                      shipment.shippingType === "REVERSE" 
                        ? "orange" 
                        : hasS3Url 
                        ? "success" 
                        : "blue"
                    }
                  >
                    <span className="shipment-number">{awbNumber}</span>
                    {shipment.shippingType === "REVERSE" && (
                      <span className="rto-shipment-text">
                        RTO shipment
                      </span>
                    )}
                  </Tag>
                </div>
              );
            })}
          </div>
        </div>

        {/* File upload section for POD */}
        <div className="file-upload-section">
            {!pageFile ? (
              <div className="upload-placeholder">
                <div className="upload-icon-wrapper">
                  <UploadOutlined className="upload-icon" />
                </div>
                <Text type="secondary" className="upload-text">
                  Upload POD for this page
                </Text>
                <Upload
                  accept="image/*,application/pdf"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handlePageFileUpload(pageNumber, file);
                    return false; // Prevent auto upload
                  }}
                  className="pod-upload-button"
                >
                  <Button 
                    type="primary" 
                    icon={<UploadOutlined />}
                    size="small"
                    loading={uploadingPod[pageNumber]}
                    className="upload-pod-btn"
                  >
                    {uploadingPod[pageNumber] ? 'Uploading...' : 'Upload POD'}
                  </Button>
                </Upload>
              </div>
            ) : (
              <div className="file-preview">
                <div className="file-info">
                  <div className="file-icon-wrapper">
                    <FileImageOutlined className="file-icon" />
                  </div>
                  <div className="file-details">
                    <Text strong className="file-name">{pageFile.file.name}</Text>
                    <Text type="secondary" className="file-size">
                      {(pageFile.file.size / 1024).toFixed(1)} KB • {pageFile.type.toUpperCase()}
                    </Text>
                  </div>
                  <div className="file-actions-column">
                    {pageFile.url && (
                      <Button
                        type="primary"
                        ghost
                        size="small"
                        onClick={() => window.open(pageFile.url, "_blank")}
                        className="preview-btn"
                      >
                        Preview
                      </Button>
                    )}
                    {/* <Button
                      type="primary"
                      ghost
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removePageFile(pageNumber)}
                      size="small"
                      className="remove-file-btn"
                    >
                      Remove
                    </Button> */}
                  </div>
                </div>
              </div>
            )}
        </div>
      </Card>
    );
  };

  if (!drsNumber) {
    return (
      <div className="delivery-update-container">
        <div className="error-state">
          <ExclamationCircleOutlined
            style={{ fontSize: 48, color: "#faad14" }}
          />
          <Title level={3}>Invalid Access</Title>
          <Text>DRS number is required to access this page.</Text>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="delivery-update-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh' 
      }}>
        <Spin
          spinning={loading}
          indicator={
            <img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />
          }
        >
        </Spin>
      </div>
    );
  }

  return (
    <div className="delivery-update-container">
      {/* Header Section */}
      <div className="drs-delivery-update-header">
        <Row>
          <Col span={24}>
            <div className="header-content">
              <div className="header-left">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                  className="back-button"
                >
                  Back
                </Button>
                <div className="page-title">
                  <div style={{ display: 'flex', flexDirection: 'column'}}>
                  <Title level={2} style={{ margin: 0 }}>
                    Upload POD Documents
                  </Title>
                    <Text type="secondary">DRS Number: {drsNumber}</Text>
                    <Text type="secondary">Sewasetu DRS Number: {sewaSetuDrsNo}</Text>
                  </div>
                </div>
              </div>

              {/* Upload PDF Button in Header */}
              {status === "delivered" && (
                <div className="header-right">
                  <Space size="middle" className="header-actions">
                    <div className="pages-info">
                      <div className="pages-count">
                        <Text type="secondary" className="pages-label">Pages Required:</Text>
                        <Tag color="blue" className="pages-tag">
                          {Object.keys(shipmentPages).length}
                        </Tag>
                      </div>
                      {Object.keys(pageFiles).length > 0 && (
                        <div className="upload-progress-mini">
                          <Text type="secondary" className="progress-text">
                            {Object.keys(pageFiles).length}/{Object.keys(shipmentPages).length} attached
                          </Text>
                        </div>
                      )}
                    </div>
                    {/* <Upload
                      beforeUpload={(file) => {
                        if (file.type !== "application/pdf") {
                          message.error("Please upload a PDF file");
                          return false;
                        }
                        handlePDFUpload(file);
                        return false;
                      }}
                      accept=".pdf"
                      showUploadList={false}
                      disabled={pdfProcessing}
                    >
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={pdfProcessing}
                        size="large"
                        className="upload-pdf-btn"
                      >
                        {pdfProcessing ? "Processing PDF..." : "Upload PDF"}
                      </Button>
                    </Upload> */}
                  </Space>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <div className="content-section">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="delivery-form"
          initialValues={{
            receiverName: receiverName,
            receiverMobile: receiverPhone,
            remarks: remarks,
          }}
        >
          {/* Form fields based on status */}
          {/* {status === "delivered" ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="receiverName"
                  label="Receiver Name"
                  rules={validationRules.receiverName}
                >
                  <Input placeholder="Enter receiver name" size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="receiverMobile"
                  label="Receiver Mobile"
                  rules={validationRules.receiverMobile}
                >
                  <Input
                    placeholder="Enter 10-digit mobile number"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Form.Item
              name="reasonCode"
              label="Reason Code"
              rules={validationRules.reasonCode}
            >
              <Select placeholder="Select reason code" size="large">
                <Option value="UDR001">Customer not available</Option>
                <Option value="UDR002">Incorrect address</Option>
                <Option value="UDR003">Customer refused</Option>
                <Option value="UDR015">Door locked, no answer</Option>
              </Select>
            </Form.Item>
          )} */}

          {/* <Form.Item
            name="remarks"
            label="Remarks"
            rules={validationRules.remarks}
          >
            <TextArea
              placeholder="Enter delivery remarks"
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item> */}

          {/* Shipment pages */}
          <div className="shipment-pages-section">
            <div className="section-header">
              <Title level={4} className="section-title">
                Shipment Pages
                <span className="section-subtitle">
                  ({Object.keys(shipmentPages).length} page{Object.keys(shipmentPages).length !== 1 ? 's' : ''})
                </span>
              </Title>
              <div className="upload-status">
                <div className="upload-status-info">
                  <Text type="secondary">
                    {Object.keys(pageS3Urls).length} of {Object.keys(shipmentPages).length} POD files uploaded
                  </Text>
                  <div className="progress-indicator">
                    <div 
                      className="progress-bar" 
                      style={{ 
                        width: `${(Object.keys(pageS3Urls).length / Object.keys(shipmentPages).length) * 100}%` 
                      }}
                    />
                  </div>
                </div>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="small"
                  loading={submitLoading}
                  icon={<CheckCircleOutlined />}
                  disabled={Object.keys(shipmentPages).some((page) => !pageS3Urls[page])}
                  className="section-update-btn"
                >
                  {submitLoading ? 'Uploading...' : 'Upload POD'}
                </Button>
              </div>
            </div>
            <Row gutter={[20, 20]} className="shipment-pages-grid">
              {Object.entries(shipmentPages).map(([pageNumber, shipments]) => (
                <Col key={pageNumber} xs={24} sm={12} lg={8} xl={6}>
                  {renderShipmentPage(pageNumber, shipments)}
                </Col>
              ))}
            </Row>
          </div>

          {/* Submit button */}
          <div className="submit-section">
            <div className="submit-container">
              <div className="submit-info">
                <Text type="secondary" className="submit-text">
                  {Object.keys(pageS3Urls).length === Object.keys(shipmentPages).length ? (
                    <span className="ready-text">
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      All POD files uploaded. Ready to submit.
                    </span>
                  ) : (
                    <span className="pending-text">
                      <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                      {Object.keys(shipmentPages).length - Object.keys(pageS3Urls).length} POD file(s) pending
                    </span>
                  )}
                </Text>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitLoading}
                icon={<CheckCircleOutlined />}
                disabled={Object.keys(shipmentPages).some((page) => !pageS3Urls[page])}
                className="submit-btn"
              >
                {submitLoading ? 'Uploading...' : 'Upload POD'}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CpDrsDeliveryUpdate;
